import React, { useState, useMemo, useCallback, useEffect } from 'react';
import useTransactions from '../../hooks/useTransactions';
import DashboardSummary from '../SummaryCard';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';
import { Transaction as TransactionType } from '../../types/transaction';

// เพิ่ม interface สำหรับ GroupedTransaction
interface GroupedTransaction {
  date: string;
  domain: string;
  timeRange: string;
  items: TransactionType[];
  totalAmount: number;
  totalFee: number;
  totalPayAmount: number;
}

// เพิ่ม interface สำหรับ GroupedHistoryItem
interface GroupedHistoryItem {
  date: string;
  domain: string;
  timeRange: string;
  items: HistoryItem[];
  totalAmount: number;
  totalFee: number;
  totalPayAmount: number;
}

// เพิ่ม interface ที่จำเป็น
interface HistoryItem {
  id: number;
  deposit_order_id: number;
  old_status: number;
  new_status: number;
  updated_by: string | null;
  updated_at: string;
  amount: string;
  fee: string;
  payAmount: string;
  extra: string;
  domain: string;
}

const Transaction: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [searchDomain, setSearchDomain] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusPaid, setStatusPaid] = useState('');
  const [status, setStatus] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [groupedPaidHistory, setGroupedPaidHistory] = useState<GroupedHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyItemsPerPage, setHistoryItemsPerPage] = useState(10);
  const [historyTableCurrentPage, setHistoryTableCurrentPage] = useState<{ [key: string]: number }>({});
  const [historyTableItemsPerPage, setHistoryTableItemsPerPage] = useState(10);

  const { data, loading, error } = useTransactions(searchDomain, startDate, endDate, statusPaid, status);

  const groupedPaidTransactions = useMemo(() => {
    if (!data?.data?.transactions) return [];

    const paidTransactions = data.data.transactions.filter((t: TransactionType) => t.status_paid);
    const grouped = paidTransactions.reduce<{ [key: string]: GroupedTransaction }>((acc, item) => {
      const date = format(new Date(item.paid_at || item.created_at), 'yyyy-MM-dd');
      const timeRange = format(new Date(item.paid_at || item.created_at), 'HH:mm');
      const key = `${date}-${item.domain}-${timeRange}`;
      
      if (!acc[key]) {
        acc[key] = {
          date,
          domain: item.domain,
          timeRange,
          items: [],
          totalAmount: 0,
          totalFee: 0,
          totalPayAmount: 0
        };
      }
      
      acc[key].items.push(item);
      acc[key].totalAmount += Number(item.amount);
      acc[key].totalFee += Number(item.fee);
      acc[key].totalPayAmount += Number(item.payAmount);
      
      return acc;
    }, {});

    return Object.values(grouped);
  }, [data]);

  const fetchPaidHistory = useCallback(async (domain: string) => {
    if (!domain) return;
    setLoadingHistory(true);
    try {
      const response = await fetch(
        `https://azpay.devng.pro/api/transactions/status-paid-history/all?domain=${domain}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let result = '';
      
      while (true) {
        const {value, done} = await reader.read();
        if (done) break;
        result += decoder.decode(value, {stream: true});
      }
      
      const data = JSON.parse(result);
      return data;
    } catch (error) {
      console.error('Error fetching paid history:', error);
      throw error;
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const groupHistoryItems = useCallback((items: HistoryItem[]) => {
    const grouped = items.reduce((acc: { [key: string]: GroupedHistoryItem }, item) => {
      const date = format(new Date(item.updated_at), 'yyyy-MM-dd');
      const timeRange = format(new Date(item.updated_at), 'HH:mm');
      const key = `${date}-${item.domain}-${timeRange}`;
      
      if (!acc[key]) {
        acc[key] = {
          date,
          domain: item.domain,
          timeRange,
          items: [],
          totalAmount: 0,
          totalFee: 0,
          totalPayAmount: 0
        };
      }
      
      acc[key].items.push(item);
      acc[key].totalAmount += parseFloat(item.amount);
      acc[key].totalFee += parseFloat(item.fee);
      acc[key].totalPayAmount += parseFloat(item.payAmount);
      
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => {
      const dateTimeA = `${a.date} ${a.timeRange}`;
      const dateTimeB = `${b.date} ${b.timeRange}`;
      return new Date(dateTimeB).getTime() - new Date(dateTimeA).getTime();
    });
  }, []);

  useEffect(() => {
    if (searchDomain) {
      fetchPaidHistory(searchDomain)
        .then(response => {
          if (response?.data) {
            const grouped = groupHistoryItems(response.data);
            setGroupedPaidHistory(grouped);
          }
        })
        .catch(error => {
          console.error('Error:', error);
        });
    }
  }, [searchDomain, fetchPaidHistory, groupHistoryItems]);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }, []);

  const handleDomainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDomain(event.target.value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSearchDomain(domain);
    setIsSubmitted(true);
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  const handleStatusPaidChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusPaid(event.target.value);
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(event.target.value);
  };

  const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleHistoryPageChange = (pageNumber: number) => {
    setHistoryCurrentPage(pageNumber);
  };

  const handleHistoryItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setHistoryItemsPerPage(Number(event.target.value));
    setHistoryCurrentPage(1);
  };

  const handleHistoryTablePageChange = (groupKey: string, pageNumber: number) => {
    setHistoryTableCurrentPage(prev => ({
      ...prev,
      [groupKey]: pageNumber
    }));
  };

  const handleHistoryTableItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setHistoryTableItemsPerPage(Number(event.target.value));
    setHistoryTableCurrentPage({}); // รีเซ็ตหน้าทั้งหมด
  };

  const getTableItems = (items: HistoryItem[], groupKey: string) => {
    const currentPage = historyTableCurrentPage[groupKey] || 1;
    const indexOfLastItem = currentPage * historyTableItemsPerPage;
    const indexOfFirstItem = indexOfLastItem - historyTableItemsPerPage;
    return {
      currentItems: items.slice(indexOfFirstItem, indexOfLastItem),
      totalPages: Math.ceil(items.length / historyTableItemsPerPage),
      currentPage
    };
  };

  const summaryData = useMemo(() => {
    if (!data) return {
      totalPayAmount: 0,
      successfulTotalPayAmount: 0,
      unsuccessfulTotalPayAmount: 0,
      totalNetAmount: 0,
      successfulTotalNetAmount: 0,
      unsuccessfulTotalNetAmount: 0,
      totalFee: 0,
      successfulTotalFee: 0,
      unsuccessfulTotalFee: 0,
      totalUnpaidAmount: 0,
      totalPaidAmount: 0,
      transactionCount: 0
    };

    const successfulTransactions = data.data.transactions.filter(t => t.status === 1);
    const unsuccessfulTransactions = data.data.transactions.filter(t => t.status !== 1);

    return {
      totalPayAmount: data.data.summary.total_payAmount,
      successfulTotalPayAmount: successfulTransactions.reduce((sum, t) => sum + t.payAmount, 0),
      unsuccessfulTotalPayAmount: unsuccessfulTransactions.reduce((sum, t) => sum + t.payAmount, 0),
      totalNetAmount: data.data.summary.total_amount,
      successfulTotalNetAmount: successfulTransactions.reduce((sum, t) => sum + t.amount, 0),
      unsuccessfulTotalNetAmount: unsuccessfulTransactions.reduce((sum, t) => sum + t.amount, 0),
      totalFee: data.data.summary.total_fee,
      successfulTotalFee: successfulTransactions.reduce((sum, t) => sum + t.fee, 0),
      unsuccessfulTotalFee: unsuccessfulTransactions.reduce((sum, t) => sum + t.fee, 0),
      totalUnpaidAmount: successfulTransactions.filter(t => !t.status_paid).reduce((sum, t) => sum + t.amount, 0),
      totalPaidAmount: successfulTransactions.filter(t => t.status_paid).reduce((sum, t) => sum + t.amount, 0),
      transactionCount: data.data.count
    };
  }, [data]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = data?.data.transactions.slice(indexOfFirstItem, indexOfLastItem) || [];
  const totalPages = Math.ceil((data?.data.transactions.length || 0) / itemsPerPage);

  const historyIndexOfLastItem = historyCurrentPage * historyItemsPerPage;
  const historyIndexOfFirstItem = historyIndexOfLastItem - historyItemsPerPage;
  const currentHistoryItems = groupedPaidHistory.slice(historyIndexOfFirstItem, historyIndexOfLastItem);
  const historyTotalPages = Math.ceil(groupedPaidHistory.length / historyItemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const Pagination = () => {
    return (
      <div className="flex justify-center items-center space-x-2 mt-4">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          ก่อนหน้า
        </button>
        
        {[...Array(totalPages)].map((_, index) => (
          <button
            key={index + 1}
            onClick={() => handlePageChange(index + 1)}
            className={`px-4 py-2 border rounded-md ${
              currentPage === index + 1 ? 'bg-blue-500 text-white' : 'hover:bg-gray-50'
            }`}
          >
            {index + 1}
          </button>
        ))}
        
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          ถัดไป
        </button>
      </div>
    );
  };

  const dailySummaries = useMemo(() => {
    if (!data?.data.transactions) return [];

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // Filter transactions for current month and group by date
    return data.data.transactions
      .filter(t => new Date(t.created_at) >= firstDayOfMonth)
      .reduce<{
        [key: string]: {
          date: string;
          paid: number;
          unpaid: number;
          paidCount: number;
          unpaidCount: number;
        };
      }>((acc, t) => {
        const date = format(new Date(t.created_at), 'yyyy-MM-dd');
        
        if (!acc[date]) {
          acc[date] = {
            date,
            paid: 0,
            unpaid: 0,
            paidCount: 0,
            unpaidCount: 0
          };
        }
        
        if (t.status_paid) {
          acc[date].paid += Number(t.amount);
          acc[date].paidCount++;
        } else {
          acc[date].unpaid += Number(t.amount);
          acc[date].unpaidCount++;
        }
        
        return acc;
      }, {});
  }, [data]);

  if (loading) return <div>กำลังโหลด...</div>;
  if (error) return <div>เกิดข้อผิดพลาด: {error}</div>;

  if (!isSubmitted || !searchDomain) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">รายงานธุรกรรมตำหรับ Domain: {searchDomain}</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="domain" className="block text-sm font-medium text-gray-700">
              กรุณาระบุ Domain
            </label>
            <input
              type="text"
              id="domain"
              value={domain}
              onChange={handleDomainChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              required
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            ยืนยัน
          </button>
        </form>
      </div>
    );
  }

  if (!data || data.data.transactions.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">รายงานธุรกรรมสำหรับ Domain: {searchDomain}</h1>
        <p>ไม่พบข้อมูลธุรกรรมสำหรับ Domain นี้</p>
        <button
          onClick={() => {
            setIsSubmitted(false);
            setSearchDomain('');
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          ค้นหา Domain อื่น
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">รายงานธุรกรรมสำหรับ Domain: {searchDomain}</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">วันที่เริ่มต้น</label>
          <input
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">วันที่สิ้นสุด</label>
          <input
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">สถานะการเบิก</label>
          <select
            value={statusPaid}
            onChange={handleStatusPaidChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">ทั้งหมด</option>
            <option value="0">ยังไม่เบิก</option>
            <option value="1">เบิกแล้ว</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">สถานะการทำรายการ</label>
          <select
            value={status}
            onChange={handleStatusChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">ทั้งหมด</option>
            <option value="0">รอดำเนินการ</option>
            <option value="1">สำเร็จ</option>
          </select>
        </div>
      </div>
      <DashboardSummary data={summaryData} />
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">แสดง</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">รายการต่อหน้า</span>
          </div>
          <div className="text-sm text-gray-700">
            แสดง {indexOfFirstItem + 1} ถึง {Math.min(indexOfLastItem, data?.data.transactions.length || 0)} จาก {data?.data.transactions.length || 0} รายการ
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">จำนวนเงิน</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ค่าธรรมเนียม</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ยอดสุทธิ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่สร้าง</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เว็บไซต์</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">รายละเอียด</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentTransactions.map((transaction) => (
              <tr key={transaction.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.orderId}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {Number(transaction.amount).toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {Number(transaction.fee).toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {Number(transaction.payAmount).toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${transaction.status === 1 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {transaction.status === 1 ? 'สำเร็จ' : 'รอดำเนินการ'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"> {new Date(transaction.created_at).toLocaleString('th-TH')}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"> {transaction.domain}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"> {transaction.extra}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex justify-center items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              ก่อนหน้า
            </button>
            
            {totalPages <= 7 ? (
              [...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-4 py-2 border rounded-md ${
                    currentPage === index + 1 ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                  }`}
                >
                  {index + 1}
                </button>
              ))
            ) : (
              <>
                {[1, 2, 3].map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-4 py-2 border rounded-md ${
                      currentPage === pageNum ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
                <span className="px-4 py-2">...</span>
                {[totalPages - 2, totalPages - 1, totalPages].map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-4 py-2 border rounded-md ${
                      currentPage === pageNum ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </>
            )}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              ถัดไป
            </button>
          </div>
        </div>
      </div>
      {searchDomain && (
        <div className="mt-8">
          <h2 className="text-2xl font-bold mb-4">ประวัติการเบิกเงิน - {searchDomain}</h2>
          
          {loadingHistory ? (
            <div className="flex justify-center items-center p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              <span className="ml-4 text-gray-600">กำลังโหลดข้อมูลประวัติ...</span>
            </div>
          ) : groupedPaidHistory.length > 0 ? (
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-700">แสดง</span>
                  <select
                    value={historyItemsPerPage}
                    onChange={handleHistoryItemsPerPageChange}
                    className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-gray-700">รายการต่อหน้า</span>
                </div>
                <div className="text-sm text-gray-700">
                  แสดง {historyIndexOfFirstItem + 1} ถึง {Math.min(historyIndexOfLastItem, groupedPaidHistory.length)} จาก {groupedPaidHistory.length} รายการ
                </div>
              </div>

              <div className="space-y-4">
                {currentHistoryItems.map((group) => (
                  <div 
                    key={`${group.date}-${group.domain}-${group.timeRange}`} 
                    className="border-b last:border-b-0"
                  >
                    <div
                      onClick={() => toggleGroup(`${group.date}-${group.domain}-${group.timeRange}`)}
                      className="px-6 py-4 bg-gray-50 cursor-pointer hover:bg-gray-100 flex justify-between items-center"
                    >
                      <div className="space-y-1">
                        <h3 className="text-lg font-semibold">
                          {format(new Date(group.date), 'dd MMMM yyyy', { locale: th })}
                          <span className="ml-2 text-sm text-gray-600">
                            เวลา {group.timeRange} น.
                          </span>
                        </h3>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">จำนวนรายการ: {group.items.length}</p>
                        <p className="font-semibold">
                          ยอดรวม: {group.totalPayAmount.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                        </p>
                      </div>
                    </div>
                    
                    {expandedGroups.includes(`${group.date}-${group.domain}-${group.timeRange}`) && (
                      <div className="p-4">
                        <div className="px-6 py-4 bg-gray-50 flex justify-between items-center">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-700">แสดง</span>
                            <select
                              value={historyTableItemsPerPage}
                              onChange={handleHistoryTableItemsPerPageChange}
                              className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                            <span className="text-sm text-gray-700">รายการต่อหน้า</span>
                          </div>
                        </div>

                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">จำนวนเงิน</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ค่าธรรมเนียม</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ยอดสุทธิ</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เวลา</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {(() => {
                              const groupKey = `${group.date}-${group.domain}-${group.timeRange}`;
                              const { currentItems, totalPages, currentPage } = getTableItems(group.items, groupKey);
                              
                              return (
                                <>
                                  {currentItems.map((item) => (
                                    <tr key={item.id}>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.deposit_order_id}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {parseFloat(item.amount).toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {parseFloat(item.fee).toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {parseFloat(item.payAmount).toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {format(new Date(item.updated_at), 'HH:mm:ss', { locale: th })}
                                      </td>
                                    </tr>
                                  ))}
                                  {totalPages > 1 && (
                                    <tr>
                                      <td colSpan={5}>
                                        <div className="px-6 py-4 flex justify-center items-center space-x-2">
                                          <button
                                            onClick={() => handleHistoryTablePageChange(groupKey, currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                          >
                                            ก่อนหน้า
                                          </button>
                                          
                                          {[...Array(totalPages)].map((_, index) => (
                                            <button
                                              key={index + 1}
                                              onClick={() => handleHistoryTablePageChange(groupKey, index + 1)}
                                              className={`px-4 py-2 border rounded-md ${
                                                currentPage === index + 1 ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                                              }`}
                                            >
                                              {index + 1}
                                            </button>
                                          ))}
                                          
                                          <button
                                            onClick={() => handleHistoryTablePageChange(groupKey, currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                                          >
                                            ถัดไป
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </>
                              );
                            })()}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="px-6 py-4 bg-gray-50">
                <div className="flex justify-center items-center space-x-2">
                  <button
                    onClick={() => handleHistoryPageChange(historyCurrentPage - 1)}
                    disabled={historyCurrentPage === 1}
                    className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    ก่อนหน้า
                  </button>
                  
                  {[...Array(historyTotalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handleHistoryPageChange(index + 1)}
                      className={`px-4 py-2 border rounded-md ${
                        historyCurrentPage === index + 1 ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handleHistoryPageChange(historyCurrentPage + 1)}
                    disabled={historyCurrentPage === historyTotalPages}
                    className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    ถัดไป
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 p-8">
              ไม่พบประวัติการเบิกเงินสำหรับ domain นี้
            </div>
          )}
        </div>
      )}
      <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
        <div className="px-6 py-4 bg-gray-50">
          <h3 className="text-lg font-semibold">สรุปรายวัน (เฉพาะเดือนปัจจุบัน)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ยอดที่เบิกแล้ว</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">จำนวนรายการ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ยอดที่ยังไม่เบิก</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">จำนวนรายการ</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.values(dailySummaries)
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map((summary) => (
                  <tr key={summary.date}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(summary.date), 'dd MMMM yyyy', { locale: th })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {summary.paid.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {summary.paidCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {summary.unpaid.toLocaleString('th-TH', { style: 'currency', currency: 'THB' })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {summary.unpaidCount}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Transaction;
